import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type ControlSummary } from '../api';
import { useChat } from '../ChatEngine';

type Line = {
  id: string;
  icon: string;
  urgent?: boolean;
  text: string;
  to: string;
  cta: string;
  ask?: string;
};

// Questions générales suggérées → ouvrent l'assistant pré-rempli.
const SUGGESTIONS = [
  'Quel est mon chiffre d\'affaires ce mois-ci ?',
  'Quels produits sont en rupture de stock ?',
  'Quelles factures clients sont les plus en retard ?',
  'Résume-moi les commandes à livrer.',
];

export default function Accueil() {
  const [data, setData] = useState<ControlSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [question, setQuestion] = useState('');
  const nav = useNavigate();
  const chat = useChat();

  function reload() {
    setLoading(true);
    setError(null);
    api.getControlSummary().then(setData).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }
  useEffect(() => { reload(); }, []);

  // Ouvre une nouvelle conversation avec l'assistant, pré-remplie avec la question.
  async function ask(q: string) {
    const text = q.trim();
    if (!text) return;
    try {
      const conv = await api.createConversation();
      chat.openDock({ conv: conv.id, draft: text });
    } catch (e: any) {
      setError(e.message);
    }
  }

  function buildLines(d: ControlSummary): Line[] {
    const L: Line[] = [];
    // En tête : une alerte du Journal signale un traitement automatique qui N'A PAS eu lieu
    // (commande reçue sans devis, workflow planté). Les autres lignes décrivent du travail
    // normal ; celle-ci décrit quelque chose de cassé, donc elle passe devant.
    const alertes = d.journalAlertes ?? 0;
    if (alertes > 0)
      L.push({ id: 'journal', icon: '⚠️', urgent: true, text: `${alertes} alerte${alertes > 1 ? 's' : ''} dans le Journal — un traitement automatique n'est pas allé au bout.`, to: '/journal', cta: 'Ouvrir le Journal' });
    if (d.devisAValider > 0)
      L.push({ id: 'devis', icon: '📄', text: `Vous avez ${d.devisAValider} devis à valider aujourd'hui.`, to: '/ventes?tab=devis', cta: 'Valider les devis', ask: 'Résume-moi les devis en attente de validation.' });
    if (d.commandesATraiter > 0)
      L.push({ id: 'cmd', icon: '📦', text: `${d.commandesATraiter} commande${d.commandesATraiter > 1 ? 's sont' : ' est'} à traiter (livrer puis facturer).`, to: '/ventes?tab=commandes', cta: 'Traiter les commandes' });
    if (d.commandesEnRetard > 0)
      L.push({ id: 'cmdlate', icon: '⏰', urgent: true, text: `${d.commandesEnRetard} commande${d.commandesEnRetard > 1 ? 's sont' : ' est'} en retard de livraison.`, to: '/ventes?tab=commandes', cta: 'Voir les retards', ask: 'Quelles commandes sont en retard de livraison et pour quels clients ?' });
    if (d.facturesAValider > 0)
      L.push({ id: 'facv', icon: '🧾', text: `${d.facturesAValider} facture${d.facturesAValider > 1 ? 's brouillons' : ' brouillon'} à valider.`, to: '/ventes?tab=factures', cta: 'Valider les factures' });
    if (d.facturesImpayees > 0)
      L.push({ id: 'imp', icon: '💳', urgent: true, text: `${d.facturesImpayees} facture${d.facturesImpayees > 1 ? 's clients sont impayées' : ' client est impayée'} (échéance dépassée).`, to: '/ventes?tab=factures', cta: 'Voir les factures', ask: 'Quels sont les plus gros impayés clients et de combien ?' });
    if (d.achatsEnRetard > 0)
      L.push({ id: 'po', icon: '🚚', urgent: true, text: `${d.achatsEnRetard} commande${d.achatsEnRetard > 1 ? 's fournisseurs sont' : ' fournisseur est'} en retard de réception.`, to: '/achats', cta: 'Voir les achats', ask: 'Quelles commandes fournisseurs sont en retard de réception ?' });
    if (d.stockRuptures > 0)
      L.push({ id: 'rup', icon: '📉', urgent: true, text: `${d.stockRuptures} produit${d.stockRuptures > 1 ? 's sont' : ' est'} en rupture de stock.`, to: '/stocks?statut=rupture', cta: 'Voir les stocks', ask: 'Quels produits sont en rupture de stock ?' });
    return L;
  }

  const lines = data ? buildLines(data) : [];
  const nActions = lines.length;

  const today = new Date().toLocaleDateString('fr-CH', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  // Cartes KPI construites à partir des vrais compteurs du centre de contrôle.
  // Chaque carte mène à l'écran qui permet de traiter le chiffre affiché.
  const kpis = data
    ? [
        { label: 'Devis en attente', value: data.devisAValider, hint: 'à valider', danger: false, to: '/ventes?tab=devis' },
        { label: 'Commandes à traiter', value: data.commandesATraiter, hint: 'à livrer / facturer', danger: false, to: '/ventes?tab=commandes' },
        { label: 'Factures impayées', value: data.facturesImpayees, hint: 'échéance dépassée', danger: data.facturesImpayees > 0, to: '/ventes?tab=factures' },
        { label: 'Ruptures de stock', value: data.stockRuptures, hint: 'à réapprovisionner', danger: data.stockRuptures > 0, to: '/stocks?statut=rupture' },
      ]
    : [];

  const assistantPanel = (
    <div className="assistant-ask-box">
      <p className="assistant-ask-title">💬 Demandez à l'assistant</p>
      <div className="assistant-suggestions">
        {SUGGESTIONS.map((q) => (
          <button key={q} className="assistant-chip" onClick={() => ask(q)}>{q}</button>
        ))}
      </div>
      <form className="assistant-input" onSubmit={(e) => { e.preventDefault(); ask(question); setQuestion(''); }}>
        <input placeholder="Posez votre question…" value={question} onChange={(e) => setQuestion(e.target.value)} />
        <button type="submit" disabled={!question.trim()}>Envoyer</button>
      </form>
    </div>
  );

  return (
    <div className="accueil">
      <div className="accueil-hero">
        <button className="accueil-hero-refresh" onClick={reload} disabled={loading}>↻ Rafraîchir</button>
        <div className="accueil-hero-date">{today}</div>
        <h1>Bonjour 👋</h1>
        <p>
          Voici votre activité du jour.{' '}
          {nActions > 0 ? (
            <><strong>{nActions} action{nActions > 1 ? 's' : ''}</strong> vous {nActions > 1 ? 'attendent' : 'attend'} — traitez-les d'un clic ou demandez à l'assistant.</>
          ) : (
            <>Tout est à jour — rien à traiter pour le moment. 🎉</>
          )}
        </p>
      </div>

      {loading && <div className="devis-empty">Chargement…</div>}
      {error && !loading && <div className="devis-empty err">Erreur : {error}</div>}

      {data && !loading && (
        <>
          <div className="kpi-grid">
            {kpis.map((k) => (
              <Link key={k.label} to={k.to} className={`kpi-card kpi-link ${k.danger ? 'kpi-danger' : ''}`}>
                <div className="kpi-label">{k.label}</div>
                <div className="kpi-value">{k.value}</div>
                <div className="kpi-hint">{k.hint}</div>
              </Link>
            ))}
          </div>

          <div className="accueil-grid">
            <div>
              <div className="accueil-col-title">Priorités du jour</div>
              <div className="assistant-feed">
                {lines.length === 0 && (
                  <div className="assistant-line">
                    <span className="assistant-ico">🎉</span>
                    <div className="assistant-body"><p>Tout est à jour — rien à traiter aujourd'hui.</p></div>
                  </div>
                )}
                {lines.map((l) => (
                  <div key={l.id} className={`assistant-line ${l.urgent ? 'urgent' : ''}`}>
                    <span className="assistant-ico">{l.icon}</span>
                    <div className="assistant-body">
                      <p>{l.text}</p>
                      <div className="assistant-actions">
                        <button className="assistant-btn" onClick={() => nav(l.to)}>{l.cta}</button>
                        {l.ask && <button className="assistant-ask" onClick={() => ask(l.ask!)}>💬 Demander à l'assistant</button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {assistantPanel}
          </div>
        </>
      )}

      {!data && !loading && assistantPanel}
    </div>
  );
}
